import { useMemo } from "react";
import {
    ALLOCATION_DIMENSION_ID_PREFIX,
    ATTRIBUTE_METRICS_THRESHOLD,
    COLUMN_DOOR_DELIVERY_THRESHOLD,
    COLUMN_METRICS_DELIVERY_THRESHOLD,
} from "constants/product-collection";
import { IAllocationDimension, IAllocationDimensionValue } from "helpers/allocation-dimension";
import { Metrics, MetricsKind } from "helpers/metrics/types";
import { IDropdownOption } from "@nuorder/components";
import { MerchantDropdownOptions, selectedDimensionValues } from "components";
import { EditAssortmentDialogProps } from "./tabs/doors/edit-assortment-dialog";
import { CollectionType } from "containers/folders/types/assortment";
import { AssortmentType } from "containers/folders/types/base";
import { Attribute, Dimension, DimensionsPanelReduxState } from "reducers/dimensions-panel/dimensions-panel";
import { WindowAnalytics } from "helpers/window-analytics";

export const sanitizeDimensionAttribute = {
    fieldName: (key: string) =>
        key
            .trim()
            .toLocaleLowerCase()
            .replace(/\s{2,}/g, " ")
            .replace(/\s+/g, "_"),
    displayName: (name) => name.trim().charAt(0).toUpperCase() + name.trim().slice(1),
};

export const shouldDisableAddGrouping = (
    numberOfCustomOptions: number,
    customAttributesLimit: number | undefined,
    defaultMaxCustomAttributes: number
) => {
    return numberOfCustomOptions >= (customAttributesLimit ?? defaultMaxCustomAttributes);
};

export const isColumnUnderThreshold = (
    deliveryCount: number,
    doorCount: number,
    attributes: Attribute[],
    metricsWeight
) => {
    const attributeCount = attributes.length;

    // Enforce this when delivery count > 3 and attribute is > 2
    if (deliveryCount > 3 && attributeCount > 2) {
        return false;
    }

    // Limit metrics when attribute count is > 2
    if (attributeCount > 2 && metricsWeight >= 4) {
        return false;
    }

    // Enforce this when attribute is > 3
    if (attributeCount > 3) {
        return false;
    }

    /**
     * We calculate an attributeBurden index that weighs leading attributes more, e.g
     * 1st attribute = 10 values
     * 2st attribute = 8 values
     * 3rd attribute = 20 values
     * attributeBurden = (10 * 3) + (8 * 2) + (20 * 1) = 66
     *
     * attributeBurden should be less than threshold ATTRIBUTE_METRICS_THRESHOLD
     */
    const attributeBurden = attributes.reduce((acc, attribute, i) => {
        const numOfValues = attribute.values.length;
        const weight = attributeCount - i;
        return acc + numOfValues * weight;
    }, 0);

    const columnsValue =
        deliveryCount * attributeCount * (50 * attributeCount) + deliveryCount * 0.5 * metricsWeight * (doorCount || 1);
    const isAttributeBurdenUnderThreshold = attributeBurden < ATTRIBUTE_METRICS_THRESHOLD;
    const isColumnsValueUnderThreshold = columnsValue <= COLUMN_DOOR_DELIVERY_THRESHOLD;
    const isMetricsWeightUnderThreshold = metricsWeight <= COLUMN_METRICS_DELIVERY_THRESHOLD;
    return isColumnsValueUnderThreshold && isMetricsWeightUnderThreshold && isAttributeBurdenUnderThreshold;
};

export const generateMetricsActiveList = (metrics: Metrics) => {
    return metrics
        .map((metric) => {
            if (!!metric.showSubtotals || !!metric.showTotals || !!metric.showValue) {
                return metric.kind;
            }
        })
        .filter(Boolean);
};

const MetricsWeightMap: Record<MetricsKind, number> = {
    UNITS: 1,
    AVERAGE_UNITS: 1,
    STORE_WITH_UNITS: 1,
    TOTAL_COST: 1,
    TOTAL_RETAIL: 1,
    PLAN_ROWS: 1.5,
    IMU_PERCENTAGE: 2.5,
    AUR: 2,
    SKUS: 1,
    UNIT_ROW_PERCENTAGE_PLAN: 1.5,
    ROW_PERCENTAGE_PLAN: 1.5,
    UNIT_DOOR_PERCENTAGE_PLAN: 1.5,
    DOOR_PERCENTAGE_PLAN: 1.5,
    BREADTH_PERCENTAGE_PLAN: 1.5,
    AVERAGE_DEPTH: 1.5,
    AVERAGE_COST: 1.5,
} as const;

export const generateWeightByKindOfMetric = (metricsKind: MetricsKind[]) =>
    metricsKind.reduce(
        (previousValue, kind) => (MetricsWeightMap[kind] ? previousValue + MetricsWeightMap[kind] : 0),
        0
    );

export const shouldUpdateDoorCountFromMetricsTotals = (
    metrics: Metrics,
    kindOfMetricsSelected: MetricsKind[]
): boolean => {
    const metricsSelected = metrics.filter((metric) => kindOfMetricsSelected.includes(metric.kind));

    return metricsSelected.some((metric) => metric.showSubtotals || metric.showTotals);
};

type DimensionValueAccumulator = {
    selectedOptions: IDropdownOption[];
    selectedOptionsIds: string[];
};

export type DimensionsOptions = {
    dimensionsDropdownOptions: MerchantDropdownOptions[];
    dimensionsValues: selectedDimensionValues;
};
/**
 * We need to merge the merchant allocation dimension with the allocation dimensions from the assortment
 */
export const createDimensionsOptions = (
    merchantDimensions: IAllocationDimension[],
    allocationDimensionsOptions: Dimension[]
): DimensionsOptions =>
    allocationDimensionsOptions.reduce(
        (acc, currentDimensionInAssortment) => {
            const sanitizedDimensionId = currentDimensionInAssortment.id.split(ALLOCATION_DIMENSION_ID_PREFIX)[1];
            const { selectedOptions, selectedOptionsIds } = currentDimensionInAssortment.values.reduce(
                (accumulator, value) => {
                    accumulator.selectedOptions.push({
                        id: value.id,
                        text: value.label,
                        key: value.id,
                        disabled: true,
                    });
                    accumulator.selectedOptionsIds.push(value.id);
                    return accumulator;
                },
                { selectedOptions: [], selectedOptionsIds: [] } as DimensionValueAccumulator
            );
            const currentDimensionFromMerchant = merchantDimensions.find((merchDimension) => {
                return merchDimension.id === sanitizedDimensionId;
            });
            const unselectedOptions: IDropdownOption[] = [];

            currentDimensionFromMerchant.dimension_values.forEach((value) => {
                if (!selectedOptionsIds.includes(value.id)) {
                    unselectedOptions.push({
                        id: value.id,
                        key: value.id,
                        text: value.name,
                    });
                }
            });
            acc.dimensionsValues[sanitizedDimensionId] = selectedOptionsIds;
            acc.dimensionsDropdownOptions.push({
                id: sanitizedDimensionId,
                name: currentDimensionInAssortment.name,
                values: selectedOptions.concat(unselectedOptions),
            });
            return acc;
        },
        { dimensionsDropdownOptions: [], dimensionsValues: {} } as DimensionsOptions
    );

export const parseFormStateIntoMutationPayload =
    (merchantDimensions: IAllocationDimension[], dimensionsDropdownOptions: MerchantDropdownOptions[]) =>
    (selectedDimensionIds: selectedDimensionValues): Record<string, IAllocationDimensionValue[]> =>
        Object.keys(selectedDimensionIds).reduce(
            (acc, dimensionId) => {
                // this is the list of options with the previously selected values disabled
                const { values: dropdownOptions } = dimensionsDropdownOptions.find(
                    (dimension) => dimension.id === dimensionId
                );
                // we need to remove the ids previously selected
                const newlySelectedValuesIds = selectedDimensionIds[dimensionId].filter(
                    (valueId) => !dropdownOptions.find((option) => option.id === valueId).disabled
                );
                const { dimension_values } = merchantDimensions.find((md) => md.id === dimensionId);

                return {
                    ...acc,
                    [`${ALLOCATION_DIMENSION_ID_PREFIX}${dimensionId}`]: newlySelectedValuesIds.map((id) => ({
                        ...dimension_values.find((md) => md.id === id),
                        included: true,
                    })),
                };
            },
            {} as Record<string, IAllocationDimensionValue[]>
        );

export type parseFormStateIntoMutationPayloadOutput = ReturnType<typeof parseFormStateIntoMutationPayload>;

export const useEditDialogInitialState = (
    isFetchingMerchantDimensions: boolean,
    merchantDimensions: IAllocationDimension[],
    allocationDimensionsOptions: Dimension[],
    driveItem?: nuorder.folder.v1.Item
) =>
    useMemo(() => {
        if (isFetchingMerchantDimensions || !driveItem) {
            return {} as Pick<EditAssortmentDialogProps, "dimensionsDropdownOptions" | "formValues">;
        }
        const { dimensionsValues, dimensionsDropdownOptions } = createDimensionsOptions(
            merchantDimensions,
            allocationDimensionsOptions
        );
        const isExternalAssortment = driveItem.collection_facet?.collection_type === CollectionType.External;
        const brandName = driveItem.collection_facet?.brand_portal_name;
        return {
            dimensionsDropdownOptions,
            formValues: {
                dimensionsValues,
                assortmentType: isExternalAssortment ? AssortmentType.External : AssortmentType.Internal,
                brandId: brandName ? brandName : undefined,
            },
        };
    }, [isFetchingMerchantDimensions, merchantDimensions, allocationDimensionsOptions, driveItem]);

/**
 * Helper function to send analytics. Analytics is only sent when doors is turned on
 * @param dimensionsPanelState
 * @param isRollupMode
 */
export const trackDimensionsPanelAnalytics = (
    dimensionsPanelState: DimensionsPanelReduxState,
    isRollupMode: boolean
) => {
    // It's always the first dimension if there is more than one dimension
    const deliveryDimension =
        dimensionsPanelState.dimensions.length > 1 ? dimensionsPanelState.dimensions[0] : undefined;
    const deliveryCount = deliveryDimension?.selected
        ? deliveryDimension.values.filter((value) => value.selected).length
        : 0;

    // It's always the last dimension
    const doorDimension = dimensionsPanelState.dimensions[dimensionsPanelState.dimensions.length - 1];

    const doorCount = doorDimension?.selected ? doorDimension.values.filter((value) => value.selected).length : 0;
    const attributeCount = dimensionsPanelState.attributes.filter((attribute) => attribute.selected).length;

    if (dimensionsPanelState.showDoors) {
        WindowAnalytics.track("Dimension and Attribute Selections", {
            doors: doorCount,
            attributes: attributeCount,
            isRollupMode,
            ...(deliveryDimension ? { deliveries: deliveryCount } : {}),
        });
    }
};
